import {
  DepositsManagerStatsResponse,
  TransactionNative,
  TransactionToken,
} from '@/submodules/v3-shared/ts/connect/swell/v3/operator_pb'
import { TwitterUserResponse } from '@v3-backend/twitter_pb'
import { Reprice } from '@v3-backend/rates_pb'
import { Stake, StakeTransactionType } from '@v3-backend/staker_pb'
import { EarnAPY, EarnAPYsResponse } from '@v3-backend/stats_pb'
import {
  VoyageLeaderboardRecord,
  VoyageStatusResponse,
  VoyageUserResponse,
} from '@v3-backend/voyage_pb'
import { AppEarnAPRsResponse, TokenPoolExternal } from '@/types/earn'
import { AppReprice } from '@/types/rates'
import { AppStake } from '@/types/stake'
import { Token, TokenList } from '@/types/tokens'
import { AppTransactionNative, AppTransactionToken } from '@/types/transaction'
import { AppTwitterUser } from '@/types/twitter'
import {
  AppEigenPointsUser,
  AppVoyageLeaderboardRecord,
  AppVoyageStatus,
  AppVoyageUser,
} from '@/types/voyage'
import { Timestamp } from '@bufbuild/protobuf'
import { BigNumber, ethers } from 'ethers'
import { EigenPointsUserResponse } from '@/submodules/v3-shared/ts/connect/swell/v3/eigenpoints_pb'
import {
  PreDepositRatesResponse,
  PreDepositStatsResponse,
  TokenListResponse,
} from '@/submodules/v3-shared/ts/connect/swell/v3/predeposit_pb'

export const earnAPRsAdapter = ({
  featured,
  topApr,
  available,
}: EarnAPYsResponse): AppEarnAPRsResponse => {
  const parseEarnAPR = (earnAPY: EarnAPY): TokenPoolExternal => {
    return {
      id: earnAPY.id,
      apy: earnAPY.apy,
      category: earnAPY.category,
      chainId: earnAPY.chainId,
      chainName: earnAPY.chainName,
      chainLogo: earnAPY.chainLogo,
      includeTvl: earnAPY.includeTvl,
      link: earnAPY.link,
      logo: earnAPY.logo,
      positionName: earnAPY.positionName,
      positionTokens: earnAPY.positionTokens,
      protocol: earnAPY.protocol,
      tokenIconList: earnAPY.tokenIconList,
      tvl: earnAPY.tvl,
    }
  }

  return {
    featured: featured.map(parseEarnAPR),
    topApy: topApr.map(parseEarnAPR),
    pools: available.map(parseEarnAPR),
  }
}

/**
 * Converts "TransactionToken" data from the v3-backend APIs into a form that the frontend can use
 *
 * @param param0 TransactionToken data (received from v3-backend API)
 * @returns AppTransactionToken data (standardized format to be used in the app)
 */
export const transactionTokenAdapter = ({
  createdAt,
  value: amountWei,
  ...tx
}: TransactionToken): AppTransactionToken => {
  return {
    ...tx,
    amount: BigNumber.from(amountWei),
    createdAt: timestampAdapter(createdAt),
  }
}

/**
 * Converts "TransactionNative" data from the v3-backend APIs into a form that the frontend can use
 *
 * @param param0 TransactionNative data (received from v3-backend API)
 * @returns AppTransactionNative data (standardized format to be used in the app)
 */
export const transactionNativeAdapter = ({
  valueWei,
  createdAt,
  ...tx
}: TransactionNative): AppTransactionNative => {
  return {
    ...tx,
    keysSubmitted: valueWei,
    createdAt: timestampAdapter(createdAt),
  }
}

export function timestampAdapter(timestamp: Timestamp): number
export function timestampAdapter(
  timestamp: Timestamp | undefined
): number | undefined

/**
 * Converts protobuf timestamp into a form that is usable by the frontend
 * Gracefully handles the timestamp being undefined. Overloads ensure type invariance.
 *
 * @param timestamp Protobuf timestamp
 * @returns time in milliseconds
 */
export function timestampAdapter(timestamp: Timestamp | undefined) {
  return timestamp ? Number(timestamp.seconds) * 1000 : undefined
}

/**
 * Converts DepositsManagerStatsResponse into a form that is usable by the frontend
 *
 * @param response API response for deposit manager stats
 * @returns Transformed API response
 */
export const depositManagerStatsAdapter = (
  response: DepositsManagerStatsResponse
) => {
  const { bufferedWei, currentCapacityWei, unusedKeysCount, lastDepositAt } =
    response

  return {
    bufferedEth: BigNumber.from(bufferedWei),
    currentCapacityEth: BigNumber.from(currentCapacityWei),
    unusedKeysCount: Number(unusedKeysCount),
    lastDepositAt: timestampAdapter(lastDepositAt),
  }
}

/**
 * Converts "VoyageLeaderboardRecord" data from the v3-backend APIs into a form that the frontend can use
 *
 * @param param0 VoyageLeaderboardRecord data (received from v3-backend API)
 * @returns AppVoyageLeaderboardRecord data (standardized format to be used in the app)
 */
export const voyageLeaderboardRecordAdapter = ({
  address,
  points,
}: VoyageLeaderboardRecord): AppVoyageLeaderboardRecord => {
  return {
    address,
    points: Number(points),
  }
}

export const voyageUserAdapter = ({
  address,
  points,
  swethReferred,
  numReferrals,
}: VoyageUserResponse): AppVoyageUser => {
  return {
    address,
    points: Number(points),
    numReferrals,
    swethReferred,
  }
}

export const voyageStatusAdapter = ({
  totalPearls,
  totalUsers,
}: VoyageStatusResponse): AppVoyageStatus => {
  return {
    totalPearls,
    totalUsers,
  }
}

export const eigenPointsUserAdapter = ({
  address,
  points,
}: EigenPointsUserResponse): AppEigenPointsUser => {
  return {
    address,
    points: Number(points),
  }
}

/**
 * Converts StakesResponse into a form known by the frontend
 *
 * @param Stake (from staker_pb)
 * @returns AppStake
 */
export const stakesAdapter = (response: Stake): AppStake => {
  const {
    address,
    mintedSweth,
    stakedWei,
    timestamp,
    txHash,
    stakeTransactionType,
  } = response

  return {
    address,
    mintedToken: BigNumber.from(mintedSweth),
    stakedWei: BigNumber.from(stakedWei),
    timestamp: Number(timestamp) * 1000,
    txHash,
    stakeTransactionType:
      stakeTransactionType === StakeTransactionType.RESTAKE
        ? 'restake'
        : 'stake',
  }
}

export const repriceAdapter = (response: Reprice): AppReprice => {
  const { rate, time, wethUsdc, blockNumber } = response

  return {
    blockNumber: blockNumber ? Number(blockNumber) : -1,
    rate: BigNumber.from(rate),
    timestamp: Number(time) * 1000,
    wethUsdc: wethUsdc ? BigNumber.from(wethUsdc) : BigNumber.from('0'),
  }
}

const TWITTER_USER_ERROR_MESSAGES = {
  NO_CODE: 'Submitted tweet did not contain your personal code.',
}

export const twitterUserAdapter = (
  response: TwitterUserResponse
): AppTwitterUser => {
  const { message, user, queuePosition } = response

  const userVerificationError = user?.verificationError
  let verificationError = ''

  if (userVerificationError) {
    if (/does not contain code/.test(userVerificationError)) {
      verificationError = TWITTER_USER_ERROR_MESSAGES.NO_CODE
    }
  }

  return {
    message,
    queuePosition: queuePosition || 0,
    twitterCode: user?.twitterCode || '',
    walletAddress: user?.walletAddress || '',
    verificationStatus: user?.verificationStatus || 0,
    verificationError,
  }
}

export const tokenRatesAdapter = (
  rates: PreDepositRatesResponse
): Record<string, number> => {
  const rateMap: any = {}

  rates.ratesUsd.forEach((rate: any) => {
    try {
      rateMap[ethers.utils.getAddress(rate.tokenAddress)] = rate.rateCents / 100
    } catch (err) {
      console.error('parse token address:', rate.tokenAddress, err)
    }
  })

  return rateMap
}

export const tokenListProtobufAdapter = (
  tl: NonNullable<TokenListResponse['tokenList']>
): TokenList => {
  const tokens: Token[] = []
  for (const token of tl.tokens) {
    let address: string
    try {
      address = ethers.utils.getAddress(token.address)
    } catch (err) {
      console.error('parse token address:', token.address, err)
      continue
    }

    tokens.push({
      address,
      chainId: token.chainId,
      decimals: token.decimals,
      name: token.name,
      symbol: token.symbol,
      logoURI: token.logoUri,
      tags: token.tags,
    })
  }

  const tags: Record<string, { name: string; description: string }> = {}
  for (const { name, description } of tl.tags) {
    tags[name] = { name, description }
  }

  return {
    keywords: [],
    logoURI: '',
    name: tl.name,
    timestamp: new Date(tl.timestamp * 1000).toISOString(),
    tokens,
    version: '',
    tags,
  }
}
